import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, ArrowRight, ArrowLeft, Building2, UserPlus, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    company_name: '', phone: '', expected_users: '', referral_source: '',
  });

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const validateStep1 = () => {
    if (!formData.name.trim()) return toast.error('Full name is required');
    if (!formData.email.trim()) return toast.error('Email is required');
    if (formData.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (formData.password !== formData.confirmPassword) return toast.error('Passwords do not match');
    return true;
  };

  const validateStep2 = () => {
    if (!formData.company_name.trim()) return toast.error('Company name is required');
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1() === true) setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep2() !== true) return;
    setLoading(true);
    try {
      await register({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        company_name: formData.company_name.trim(),
        phone: formData.phone.trim() || undefined,
        expected_users: formData.expected_users ? parseInt(formData.expected_users) : undefined,
        referral_source: formData.referral_source || undefined,
      });
      toast.success('Welcome to NewSlate! Your 14-day free trial has started.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-10 h-10 bg-[#1f3b70] rounded-lg flex items-center justify-center text-white font-extrabold text-lg">N</div>
            <span className="text-2xl font-bold text-[#1f3b70]">New<span className="text-[#e68a00]">Slate</span></span>
          </div>
          <p className="text-sm text-muted-foreground">Start your 14-day free trial</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-3 mb-6 px-4">
          <div className={`flex items-center gap-2 text-sm font-medium ${step >= 1 ? 'text-[#1f3b70]' : 'text-muted-foreground'}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step > 1 ? 'bg-green-100 text-green-700' : step === 1 ? 'bg-[#1f3b70] text-white' : 'bg-muted text-muted-foreground'}`}>
              {step > 1 ? <CheckCircle className="w-4 h-4" /> : '1'}
            </div>
            Your Account
          </div>
          <div className="flex-1 h-px bg-border" />
          <div className={`flex items-center gap-2 text-sm font-medium ${step >= 2 ? 'text-[#1f3b70]' : 'text-muted-foreground'}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step === 2 ? 'bg-[#1f3b70] text-white' : 'bg-muted text-muted-foreground'}`}>2</div>
            Your Company
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            {step === 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <UserPlus className="w-5 h-5 text-[#1f3b70]" />
                  <h2 className="text-lg font-semibold">Create Your Account</h2>
                </div>
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder="John Smith"
                    data-testid="register-name"
                  />
                </div>
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="john@yourfirm.com"
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => update('password', e.target.value)}
                    placeholder="At least 6 characters"
                    data-testid="register-password"
                  />
                </div>
                <div>
                  <Label>Confirm Password *</Label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => update('confirmPassword', e.target.value)}
                    placeholder="Confirm your password"
                    data-testid="register-confirm-password"
                  />
                </div>
                <Button onClick={handleNext} className="w-full bg-[#1f3b70] hover:bg-[#16305e]" data-testid="register-next">
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5 text-[#1f3b70]" />
                  <h2 className="text-lg font-semibold">Company Details</h2>
                </div>
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => update('company_name', e.target.value)}
                    placeholder="Smith Immigration Law"
                    data-testid="register-company"
                  />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="register-phone"
                  />
                </div>
                <div>
                  <Label>Expected Number of Users</Label>
                  <Select value={formData.expected_users} onValueChange={(v) => update('expected_users', v)}>
                    <SelectTrigger data-testid="register-users"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Just me</SelectItem>
                      <SelectItem value="5">2-5 users</SelectItem>
                      <SelectItem value="10">6-10 users</SelectItem>
                      <SelectItem value="25">11-25 users</SelectItem>
                      <SelectItem value="50">25+ users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>How did you hear about us?</Label>
                  <Select value={formData.referral_source} onValueChange={(v) => update('referral_source', v)}>
                    <SelectTrigger data-testid="register-referral"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google">Google Search</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="conference">Conference / Event</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <strong>14-day free trial</strong> — Full access to all features. No credit card required.
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="register-back">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button type="submit" disabled={loading} className="flex-1 bg-[#1f3b70] hover:bg-[#16305e]" data-testid="register-submit">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Start Free Trial
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Already have an account?{' '}
          <Link to="/login" className="text-[#1f3b70] font-medium hover:underline" data-testid="register-login-link">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
